import { db, supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database.types";
import { diffRecords, fullRowChanges, recordAudit } from "@/lib/audit";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRealtimeRefresh } from "./useRealtimeRefresh";

export type Client = Tables<"clients">;

/** A project linked to a client (the subset the Clients page displays/links to). */
export interface ClientProject {
  id: string;
  name: string;
  start_date: string | null;
}

export type ClientWithProjects = Client & {
  projects: ClientProject[];
};

export type ClientInput = {
  name: string;
  location: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
};

/** Raised by deleteClient when the client still has linked projects. */
export class ClientHasProjectsError extends Error {
  constructor(public readonly count: number) {
    super(`Client has ${count} linked project${count === 1 ? "" : "s"}`);
    this.name = "ClientHasProjectsError";
  }
}

export interface UseClientsDataResult {
  clients: ClientWithProjects[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshSilently: () => Promise<void>;
  createClient: (input: ClientInput) => Promise<Client>;
  updateClient: (id: string, input: ClientInput) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
}

async function fetchClientsWithProjects(): Promise<ClientWithProjects[]> {
  const [{ data: clients, error: clientsError }, { data: projects, error: projectsError }] =
    await Promise.all([
      db.clients.getAll(),
      supabase
        .from("projects")
        .select("id, name, start_date, client_id")
        .not("client_id", "is", null),
    ]);

  if (clientsError) throw clientsError;
  if (projectsError) throw projectsError;

  const projectsByClient = new Map<string, ClientProject[]>();
  for (const p of projects ?? []) {
    if (!p.client_id) continue;
    const list = projectsByClient.get(p.client_id) ?? [];
    list.push({
      id: p.id,
      name: p.name,
      start_date: p.start_date,
    });
    projectsByClient.set(p.client_id, list);
  }

  return (clients ?? []).map((client) => ({
    ...client,
    projects: (projectsByClient.get(client.id) ?? []).sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? ""),
    ),
  }));
}

export function useClientsData(): UseClientsDataResult {
  const [clients, setClients] = useState<ClientWithProjects[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const isInitialLoad = useRef(true);

  const load = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);
    try {
      const next = await fetchClientsWithProjects();
      setClients(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load clients");
      if (!silent) setClients([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => load(false), [load]);
  const refreshSilently = useCallback(() => load(true), [load]);

  useEffect(() => {
    if (!isInitialLoad.current) return;
    isInitialLoad.current = false;
    void load(false);
  }, [load]);

  const createClient = useCallback(
    async (input: ClientInput): Promise<Client> => {
      const { data, error: insertError } = await db.clients.create(input);
      if (insertError || !data) throw insertError ?? new Error("Failed to create client");
      markSelfEdit("clients", data.id);
      recordAudit({
        table: "clients",
        recordId: data.id,
        operation: "INSERT",
        changes: fullRowChanges(data as Record<string, unknown>, "INSERT"),
        recordLabel: data.name,
        officeId: null,
      });
      await refreshSilently();
      return data;
    },
    [markSelfEdit, refreshSilently],
  );

  const updateClient = useCallback(
    async (id: string, input: ClientInput): Promise<void> => {
      const previous = clients.find((c) => c.id === id);
      markSelfEdit("clients", id);
      const { data, error: updateError } = await db.clients.update(id, input);
      if (updateError) throw updateError;
      recordAudit({
        table: "clients",
        recordId: id,
        operation: "UPDATE",
        changes: diffRecords(
          previous ? stripDerived(previous) : null,
          (data as Record<string, unknown>) ?? input,
        ),
        recordLabel: input.name,
        officeId: null,
      });
      await refreshSilently();
    },
    [clients, markSelfEdit, refreshSilently],
  );

  const deleteClient = useCallback(
    async (id: string): Promise<void> => {
      const target = clients.find((c) => c.id === id);
      if (target && target.projects.length > 0) {
        throw new ClientHasProjectsError(target.projects.length);
      }
      markSelfEdit("clients", id);
      const { error: deleteError } = await db.clients.delete(id);
      if (deleteError) {
        // 23503 = FK violation: a project was linked between our check and the delete.
        if ((deleteError as { code?: string }).code === "23503") {
          throw new ClientHasProjectsError(target?.projects.length ?? 1);
        }
        throw deleteError;
      }
      if (target) {
        recordAudit({
          table: "clients",
          recordId: id,
          operation: "DELETE",
          changes: fullRowChanges(stripDerived(target), "DELETE"),
          recordLabel: target.name,
          officeId: null,
        });
      }
      await refreshSilently();
    },
    [clients, markSelfEdit, refreshSilently],
  );

  useRealtimeRefresh(["clients", "projects"], refreshSilently, {
    skipRefetchOnSelfEdit: true,
  });

  return {
    clients,
    loading,
    error,
    refresh,
    refreshSilently,
    createClient,
    updateClient,
    deleteClient,
  };
}

/** Drop the derived `projects` field so audit diffs only see real columns. */
function stripDerived(client: ClientWithProjects): Record<string, unknown> {
  const { projects, ...row } = client;
  return row;
}
